import { useState } from 'react';
import { z } from 'zod';
import { MissingResourceError } from '#api/errors.ts';
import { useSuspenseNetworkConfigOverridesQueries, useSuspenseNetworkQuery } from '#api/networks.queries.ts';
import { DeleteIcon } from '#assets/ReactSvg.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { CopyPill } from '#components/atoms/pill/copy-pill/CopyPill.tsx';
import { Tooltip } from '#components/atoms/tooltip/Tooltip.tsx';
import { MainLayout } from '#components/patterns/layouts/main/MainLayout.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import { useReturnNavigation } from '#hooks/useAppLocation.ts';
import { useZodParams } from '#hooks/useZodParams.ts';
import { SKIP_UNSAVED_CHANGES_PROMPT } from '#screens/roles/detail/form/BlockNavigationDialog.tsx';
import { splitOverrides } from '#screens/tags/hooks/split-overrides.ts';
import { getCidrV4, getCidrV6 } from '#util/networkCidrs.ts';
import { fromArray, Titled } from '#util/title.ts';
import { DeleteNetworkModal } from '../delete/DeleteNetworkModal.tsx';
import { NetworkForm } from '../form/NetworkForm.tsx';
import { UpdateNetworkModal } from '../update/UpdateNetworkModal.tsx';

export function EditNetworkPage() {
  const { networkId } = useZodParams({
    networkId: z.string().refine((id): id is `network-${string}` => id.startsWith('network-')),
  });
  const {
    data: { data: network },
    error,
    isFetching,
  } = useSuspenseNetworkQuery(networkId);
  const [showDeletionModal, setShowDeletionModal] = useState<boolean>(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const returnTo = useReturnNavigation('/settings/networks');

  const configOverridesResults = useSuspenseNetworkConfigOverridesQueries([networkId]);
  const configOverrides = splitOverrides(configOverridesResults[0]?.data.data.overrides ?? []);

  // Check if this has been deleted (e.g. in a different tab)
  if (error && error instanceof MissingResourceError && !isFetching) {
    throw error;
  }

  return (
    <Titled title={(title) => fromArray([network.name, title])}>
      <MainLayout
        header={
          <PageHeader
            title={
              <>
                <Link to="/settings/networks" className="a">
                  Networks
                </Link>
                {' / '}
                {network.name}
              </>
            }
            subtitle={
              <>
                ID: <CopyPill value={network.id} />
              </>
            }
          >
            <Spacer>
              {network.certVersion === 1 ?
                <Button size="small" variant="secondary" onClick={() => setShowUpdateModal(true)}>
                  Update network
                </Button>
              : null}
              <Tooltip
                content="Delete"
                render={
                  <Button
                    size="small"
                    variant="icon"
                    onClick={() => {
                      setShowDeletionModal(true);
                    }}
                  />
                }
              >
                <DeleteIcon aria-hidden="true" />
                <span className="visually-hidden">Delete</span>
              </Tooltip>
            </Spacer>
          </PageHeader>
        }
      >
        <NetworkForm
          networkId={networkId}
          certVersion={network.certVersion}
          onOpenUpdate={network.certVersion === 1 ? () => setShowUpdateModal(true) : undefined}
          hostCount={network.hostCount}
          defaultValues={{
            networkName: network.name,
            description: network.description,
            cidrV4: getCidrV4(network) ?? '',
            cidrV6: getCidrV6(network) ?? '',
            lighthousesAsRelays: network.lighthousesAsRelays,
            curveP256: network.curve === 'P256',
            useManagedLighthouses: !network.disableManagedLighthouses,
            configOverrides,
          }}
          onSuccess={() => {
            returnTo();
          }}
        />
        {showDeletionModal ?
          <DeleteNetworkModal
            networkId={networkId}
            onDelete={() => {
              returnTo({ state: SKIP_UNSAVED_CHANGES_PROMPT });
            }}
            onClose={() => {
              setShowDeletionModal(false);
            }}
          />
        : null}
        {showUpdateModal ?
          <UpdateNetworkModal
            networkId={networkId}
            onClose={() => setShowUpdateModal(false)}
            onUpdate={() => setShowUpdateModal(false)}
          />
        : null}
      </MainLayout>
    </Titled>
  );
}
